import React, { useState, useEffect, useCallback } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Upload, Search, CheckCircle2, XCircle, Loader2, Banknote,
  ArrowRightLeft, FileSpreadsheet, Eye, DollarSign, Clock, RotateCcw,
  AlertTriangle, Zap, History, Link, RefreshCw
} from 'lucide-react';
import {
  importarCartola, getMovimientos, autoMatch, aprobarMasivo,
  aprobarConciliacion, descartarMovimiento, getResumenConciliacion,
  getHistorialConciliaciones, linkifyStatus, linkifyCobros, linkifySyncEstados
} from '../../lib/gestion4satApi';
import { toast } from 'sonner';

function fmt(n) { return (n || 0).toLocaleString('es-CL'); }

const certezaColors = {
  alta: 'bg-green-500/10 text-green-600 border-green-500/20',
  media: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  baja: 'bg-red-500/10 text-red-600 border-red-500/20',
};

export default function G4sConciliacionPage() {
  const [tab, setTab] = useState('conciliar');
  const [loading, setLoading] = useState(false);
  const [resumen, setResumen] = useState(null);

  // Linkify
  const [linkifyConnected, setLinkifyConnected] = useState(false);
  const [linkifyCobrosData, setLinkifyCobrosData] = useState([]);
  const [linkifySyncing, setLinkifySyncing] = useState(false);

  // Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Match
  const [matches, setMatches] = useState(null);
  const [matching, setMatching] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState(new Set());

  // Movimientos
  const [movimientos, setMovimientos] = useState([]);
  const [movTotal, setMovTotal] = useState(0);
  const [movFiltro, setMovFiltro] = useState('pendiente');
  const [movSearch, setMovSearch] = useState('');

  // Historial
  const [historial, setHistorial] = useState([]);

  const loadResumen = useCallback(async () => {
    try {
      setResumen(await getResumenConciliacion());
    } catch { /* ignore */ }
  }, []);

  const loadMovimientos = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getMovimientos({ estado: movFiltro, q: movSearch, limit: '200' });
      setMovimientos(d.movimientos);
      setMovTotal(d.total);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [movFiltro, movSearch]);

  const loadHistorial = useCallback(async () => {
    try {
      const d = await getHistorialConciliaciones();
      setHistorial(d.conciliaciones);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadResumen(); loadLinkify(); }, [loadResumen]);

  const loadLinkify = async () => {
    try {
      const status = await linkifyStatus();
      setLinkifyConnected(status.connected);
      if (status.connected) {
        const cobrosRes = await linkifyCobros();
        setLinkifyCobrosData(cobrosRes.cobros || []);
      }
    } catch { /* ignore */ }
  };

  const handleLinkifySync = async () => {
    setLinkifySyncing(true);
    try {
      const r = await linkifySyncEstados();
      toast.success(`${r.actualizados} cobros actualizados de ${r.total_revisados} revisados`);
      loadLinkify();
    } catch (e) { toast.error(e.message); }
    setLinkifySyncing(false);
  };
  useEffect(() => {
    if (tab === 'movimientos') loadMovimientos();
    if (tab === 'historial') loadHistorial();
  }, [tab, loadMovimientos, loadHistorial]);

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await importarCartola(file);
      setImportResult(r);
      toast.success(`${r.movimientos_importados} movimientos importados`);
      loadResumen();
    } catch (e) { toast.error(e.message); }
    setImporting(false);
  };

  const handleAutoMatch = async () => {
    setMatching(true);
    try {
      const r = await autoMatch();
      setMatches(r);
      setSelectedMatches(new Set(r.matches.filter(m => m.certeza === 'alta').map(m => m.movimiento_id)));
      toast.success(`${r.total_matches} matches encontrados`);
    } catch (e) { toast.error(e.message); }
    setMatching(false);
  };

  const toggleMatch = (movId) => {
    setSelectedMatches(prev => {
      const next = new Set(prev);
      if (next.has(movId)) next.delete(movId);
      else next.add(movId);
      return next;
    });
  };

  const handleAprobarSeleccionados = async () => {
    if (selectedMatches.size === 0) { toast.error('Selecciona al menos un match'); return; }
    const items = matches.matches
      .filter(m => selectedMatches.has(m.movimiento_id))
      .map(m => ({
        movimiento_id: m.movimiento_id,
        lote_id: m.factura.lote_id,
        empresa_id: m.factura.empresa_id,
      }));
    try {
      const r = await aprobarMasivo(items);
      toast.success(`${r.aprobados} conciliaciones aprobadas`);
      setMatches(null);
      loadResumen();
    } catch (e) { toast.error(e.message); }
  };

  const handleDescartar = async (movId) => {
    try {
      await descartarMovimiento(movId);
      toast.success('Movimiento descartado');
      loadMovimientos();
      loadResumen();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <G4sLayout>
      <div className="space-y-4" data-testid="g4s-conciliacion-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Conciliaci&oacute;n Bancaria</h1>
          <p className="text-sm text-muted-foreground">Cruza pagos bancarios con facturas emitidas</p>
        </div>

        {/* KPIs */}
        {resumen && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-4 flex items-center gap-3">
              <Banknote className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-xl font-black tabular-nums">{resumen.total_movimientos}</div>
                <div className="text-xs text-muted-foreground">Movimientos</div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-500" />
              <div>
                <div className="text-xl font-black tabular-nums">{resumen.pendientes}</div>
                <div className="text-xs text-muted-foreground">Pendientes (${fmt(resumen.monto_pendiente)})</div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-xl font-black tabular-nums">{resumen.conciliados}</div>
                <div className="text-xs text-muted-foreground">Conciliados (${fmt(resumen.monto_conciliado)})</div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <XCircle className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-xl font-black tabular-nums">{resumen.descartados}</div>
                <div className="text-xs text-muted-foreground">Descartados</div>
              </div>
            </CardContent></Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit flex-wrap">
          {[
            { id: 'conciliar', label: 'Conciliar', icon: ArrowRightLeft },
            { id: 'movimientos', label: 'Movimientos', icon: Banknote },
            { id: 'linkify', label: 'Linkify', icon: Link },
            { id: 'historial', label: 'Historial', icon: History },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t.id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* TAB: Conciliar */}
        {tab === 'conciliar' && (
          <div className="space-y-4">
            {/* Import + Auto-Match row */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Upload className="h-4 w-4 text-blue-500" /> Importar Cartola
                  </CardTitle>
                  <CardDescription>Carga Excel o CSV con movimientos bancarios</CardDescription>
                </CardHeader>
                <CardContent>
                  <label>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                      onChange={e => handleImport(e.target.files[0])}
                      disabled={importing}
                      data-testid="file-cartola"
                    />
                    <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-500/50 transition-colors">
                      {importing ? (
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" /> Procesando...
                        </div>
                      ) : (
                        <>
                          <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                          <p className="text-sm text-muted-foreground">Click para subir cartola (.xlsx, .csv)</p>
                        </>
                      )}
                    </div>
                  </label>
                  {importResult && (
                    <div className="mt-2 p-2 bg-green-500/10 rounded text-sm text-green-600 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      {importResult.movimientos_importados} movimientos importados
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="h-4 w-4 text-amber-500" /> Motor de Match
                  </CardTitle>
                  <CardDescription>Cruza autom&aacute;ticamente movimientos con facturas por RUT, monto y glosa</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center py-4">
                  <Button onClick={handleAutoMatch} disabled={matching} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-auto-match">
                    {matching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                    Ejecutar Match Autom&aacute;tico
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Analiza: RUT emisor, monto exacto, patentes/RUT en glosa
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Match Results */}
            {matches && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Resultados: {matches.total_matches} matches ({matches.movimientos_sin_match} sin match)
                    </CardTitle>
                    {selectedMatches.size > 0 && (
                      <Button onClick={handleAprobarSeleccionados} className="bg-green-600 hover:bg-green-700" data-testid="btn-aprobar-masivo">
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Aprobar {selectedMatches.size} seleccionados
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {matches.matches.length === 0 ? (
                    <p className="text-center py-6 text-muted-foreground">Sin matches encontrados. Importa movimientos o factura lotes primero.</p>
                  ) : (
                    <div className="space-y-2">
                      {matches.matches.map(m => (
                        <div
                          key={m.movimiento_id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedMatches.has(m.movimiento_id)
                              ? 'border-green-500 bg-green-500/5'
                              : 'border-border hover:border-muted-foreground/30'
                          }`}
                          onClick={() => toggleMatch(m.movimiento_id)}
                          data-testid={`match-${m.movimiento_id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            {/* Left: Bank movement */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Banknote className="h-4 w-4 text-blue-500 shrink-0" />
                                <span className="text-sm font-bold tabular-nums">${fmt(m.movimiento.monto)}</span>
                                <span className="text-xs text-muted-foreground">{m.movimiento.fecha}</span>
                              </div>
                              <p className="text-xs text-muted-foreground truncate">{m.movimiento.glosa || 'Sin glosa'}</p>
                              {m.movimiento.rut_emisor && <p className="text-xs font-mono text-muted-foreground">RUT: {m.movimiento.rut_emisor}</p>}
                            </div>

                            {/* Center: Match info */}
                            <div className="flex flex-col items-center gap-1 shrink-0 px-2">
                              <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                              <Badge className={certezaColors[m.certeza] || ''}>
                                {m.certeza} ({m.score}%)
                              </Badge>
                              <div className="flex flex-wrap gap-1 justify-center">
                                {m.criterios.map((c, i) => (
                                  <span key={i} className="text-[10px] bg-muted px-1 rounded">{c}</span>
                                ))}
                              </div>
                            </div>

                            {/* Right: Invoice */}
                            <div className="flex-1 min-w-0 text-right">
                              <div className="flex items-center justify-end gap-2 mb-1">
                                <span className="text-sm font-bold tabular-nums">${fmt(m.factura.total)}</span>
                                <FileSpreadsheet className="h-4 w-4 text-emerald-500 shrink-0" />
                              </div>
                              <p className="text-xs font-medium truncate">{m.factura.razon_social}</p>
                              <p className="text-xs text-muted-foreground">Lote {m.factura.numero_lote}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* TAB: Movimientos */}
        {tab === 'movimientos' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar en glosa, RUT..." value={movSearch} onChange={e => setMovSearch(e.target.value)} className="pl-9" />
              </div>
              <Select value={movFiltro} onValueChange={setMovFiltro}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendientes</SelectItem>
                  <SelectItem value="conciliado">Conciliados</SelectItem>
                  <SelectItem value="descartado">Descartados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-semibold">Fecha</th>
                      <th className="text-right p-3 font-semibold">Monto</th>
                      <th className="text-left p-3 font-semibold">Glosa</th>
                      <th className="text-left p-3 font-semibold hidden md:table-cell">RUT Emisor</th>
                      <th className="text-center p-3 font-semibold">Estado</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({length:5}).map((_,i) => <tr key={i} className="border-b"><td colSpan={6} className="p-3"><Skeleton className="h-8 w-full" /></td></tr>)
                    ) : movimientos.length === 0 ? (
                      <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">Sin movimientos. Importa una cartola bancaria.</td></tr>
                    ) : movimientos.map(m => (
                      <tr key={m.id} className="border-b hover:bg-muted/30">
                        <td className="p-3 text-xs font-mono">{m.fecha}</td>
                        <td className="p-3 text-right font-mono font-bold">${fmt(m.monto)}</td>
                        <td className="p-3 text-xs max-w-[200px] truncate">{m.glosa || '-'}</td>
                        <td className="p-3 text-xs font-mono hidden md:table-cell">{m.rut_emisor || '-'}</td>
                        <td className="p-3 text-center">
                          <Badge className={
                            m.estado === 'conciliado' ? 'bg-green-500/10 text-green-600' :
                            m.estado === 'descartado' ? 'bg-gray-500/10 text-gray-500' :
                            'bg-amber-500/10 text-amber-600'
                          }>{m.estado}</Badge>
                        </td>
                        <td className="p-3">
                          {m.estado === 'pendiente' && (
                            <Button size="sm" variant="ghost" className="text-red-500 h-7 px-2" onClick={() => handleDescartar(m.id)}>
                              <XCircle className="h-3 w-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {movTotal > 0 && <div className="p-3 text-xs text-muted-foreground border-t">{movTotal} movimientos total</div>}
            </Card>
          </div>
        )}

        {/* TAB: Historial */}
        {tab === 'linkify' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Link className="h-4 w-4 text-blue-500" /> Cobros Linkify
                  </CardTitle>
                  <CardDescription>
                    {linkifyConnected
                      ? 'Conectado a Linkify. Los cobros creados desde Facturación aparecen aquí.'
                      : 'No conectado a Linkify. Configura las credenciales en el .env del servidor.'}
                  </CardDescription>
                </div>
                {linkifyConnected && (
                  <Button size="sm" variant="outline" onClick={handleLinkifySync} disabled={linkifySyncing} data-testid="btn-linkify-sync">
                    {linkifySyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                    Sincronizar estados
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!linkifyConnected ? (
                <p className="text-center text-muted-foreground py-8">Sin conexión a Linkify</p>
              ) : linkifyCobrosData.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Sin cobros. Ve a Facturación → selecciona un lote → click "Linkify" para crear cobros.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-2 font-semibold">Empresa</th>
                        <th className="text-left p-2 font-semibold">RUT</th>
                        <th className="text-right p-2 font-semibold">Monto</th>
                        <th className="text-center p-2 font-semibold">Estado</th>
                        <th className="text-left p-2 font-semibold">Lote</th>
                        <th className="text-left p-2 font-semibold">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkifyCobrosData.map(c => (
                        <tr key={c.id} className="border-b hover:bg-muted/30">
                          <td className="p-2 font-medium">{c.empresa_razon_social}</td>
                          <td className="p-2 font-mono">{c.rut}</td>
                          <td className="p-2 text-right font-mono">${fmt(c.monto)}</td>
                          <td className="p-2 text-center">
                            <Badge variant="outline" className={
                              c.estado === 'pagado' ? 'border-green-500/50 text-green-600 bg-green-50' :
                              c.estado === 'cancelado' ? 'border-red-500/50 text-red-600' :
                              'border-amber-500/50 text-amber-600'
                            }>
                              {c.estado}
                            </Badge>
                          </td>
                          <td className="p-2 text-muted-foreground">{c.lote_id?.substring(0, 8)}</td>
                          <td className="p-2 text-muted-foreground">{c.created_at && new Date(c.created_at).toLocaleDateString('es-CL')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'historial' && (
          <div className="space-y-3">
            {historial.length === 0 ? (
              <Card><CardContent className="text-center py-12 text-muted-foreground">
                Sin conciliaciones realizadas a&uacute;n.
              </CardContent></Card>
            ) : historial.map(c => (
              <Card key={c.id}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">{c.empresa_razon_social || 'Empresa'}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Lote: {c.lote?.numero_lote || '-'} | {c.movimiento?.fecha || '-'} | Glosa: {(c.movimiento?.glosa || '').slice(0, 50)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold tabular-nums">${fmt(c.monto)}</div>
                    <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString('es-CL')}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </G4sLayout>
  );
}
